/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import '@neo4j-ndl/base/lib/neo4j-ds-styles.css';

import { DocumentIconOutline, FolderIconSolid } from '@neo4j-ndl/react/icons';
import { TreeView } from '@neo4j-ndl/react/next';
import { useState } from 'react';

type FileNode = {
  children?: FileNode[];
  id: string;
  title: string;
};

const TOP_FOLDER_COUNT = 7;
const SUBFOLDER_COUNT = 9;
const FILE_COUNT = 10;

/**
 * 7 top folders + 63 subfolders + 630 files = 700 items, all expanded so every
 * row is mounted and visible. Exercises the worst-case performance paths:
 * arrow-key navigation, Shift ranges, and the multiple-replace edge styling.
 */
const files: FileNode[] = Array.from(
  { length: TOP_FOLDER_COUNT },
  (_unused, folderIndex) => ({
    children: Array.from({ length: SUBFOLDER_COUNT }, (_unused, subIndex) => ({
      children: Array.from({ length: FILE_COUNT }, (_unused, fileIndex) => ({
        id: `file-${folderIndex}-${subIndex}-${fileIndex}`,
        title: `file-${folderIndex}-${subIndex}-${fileIndex}.txt`,
      })),
      id: `subfolder-${folderIndex}-${subIndex}`,
      title: `Subfolder ${folderIndex}.${subIndex}`,
    })),
    id: `folder-${folderIndex}`,
    title: `Folder ${folderIndex}`,
  }),
);

const Component = () => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const renderNode = (node: FileNode) => (
    <TreeView.Item
      key={node.id}
      id={node.id}
      title={node.title}
      defaultExpanded
      leadingVisual={
        node.children === undefined ? (
          <DocumentIconOutline />
        ) : (
          <FolderIconSolid />
        )
      }
    >
      {node.children?.map(renderNode)}
    </TreeView.Item>
  );

  return (
    <>
      <p style={{ marginBottom: '8px' }}>
        700 items, {selectedIds.length} selected
      </p>
      <TreeView
        ariaLabel="Large file explorer"
        selectionMode="multiple-replace"
        selectedIds={selectedIds}
        onSelectedIdsChange={setSelectedIds}
      >
        {files.map(renderNode)}
      </TreeView>
    </>
  );
};

export default Component;
